import { useMemo, useState } from "react";
import {
  clientsApi,
  useAddClientMutation,
  useUpdateClientMutation,
} from "../../../../api";
import { ReuseModal } from "../../ReuseModal";
import { Stack, Typography } from "@mui/material";
import { ReuseInput } from "../../ReuseInput";
import {
  EMAIL_REGEX,
  NAME_REGEX,
  NO_SCRIPT_REGEX,
  URL_REGEX,
  USER_NAME_REGEX,
} from "../../../../utils/regex";
import { Client, NewClient } from "../../../../types/apiTypes";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { useAppDispatch } from "../../../../hooks";
import { ClientTags } from "../../../../types/enums";

type Field = keyof NewClient;

type Props = {
  onClose: () => void;
  client?: Client;
};

const NOTES_MAX_LENGTH = 3000;

const validation: Record<Field, (value: string) => boolean> = {
  first_name: (v) => !v.trim() || !USER_NAME_REGEX.test(v),
  last_name: (v) => !v.trim() || !USER_NAME_REGEX.test(v),
  company_name: (v) => !v.trim() || !NAME_REGEX.test(v),
  company_domain: (v) => !v.trim() || !URL_REGEX.test(v),
  job_title: (v) => !v.trim(),
  email: (v) => !v.trim() || !EMAIL_REGEX.test(v),
  notes: (v) => v.length > NOTES_MAX_LENGTH || !NO_SCRIPT_REGEX.test(v),
};

const errorMessages: Record<Field, string> = {
  first_name: "Use letters only (A–Z)",
  last_name: "Use letters only (A–Z)",
  company_name: "Use letters only (A–Z)",
  job_title: "Job title is required",
  email: "Valid email is required",
  company_domain: "Valid url is required",
  notes:
    "The text length must not exceed 100 characters and cannot include scripts.",
};

const isKeyOfValidation = (field: string): field is Field => {
  return field in validation;
};

const requiredFields: Field[] = [
  "first_name",
  "last_name",
  "company_name",
  "job_title",
  "email",
  "company_domain",
];

export const AddClientModal = ({ client, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<NewClient>({
    first_name: client?.first_name ?? "",
    last_name: client?.last_name ?? "",
    company_name: client?.company_name ?? "",
    company_domain: client?.company_domain ?? "",
    job_title: client?.job_title ?? "",
    email: client?.email ?? "",
    notes: client?.notes ?? "",
  });

  const [errors, setErrors] = useState<Record<Field, boolean>>({
    first_name: false,
    last_name: false,
    company_name: false,
    job_title: false,
    company_domain: false,
    email: false,
    notes: false,
  });

  const [createClient, { isLoading: createClientLoading }] =
    useAddClientMutation();
  const [updateClient, { isLoading: updateClientLoading }] =
    useUpdateClientMutation();

  const isLoading = createClientLoading || updateClientLoading;

  const inputChangeHandler = (field: string, value: string) => {
    if (isKeyOfValidation(field)) {
      setFormData((prev) => ({ ...prev, [field]: value }));

      setErrors((prev) => ({
        ...prev,
        [field]: validation[field](value),
      }));
    }
  };

  const submitHandler = () => {
    if (client) {
      updateClient({ ...formData, client_id: client.id })
        .unwrap()
        .then(() => {
          onClose();
          dispatch(clientsApi.util.invalidateTags([ClientTags.clients]));
        })
        .catch((err) => {
          toast.error(
            <CustomToast
              type={"error"}
              text={
                getErrorMessage(err).globalError ||
                "Something went wrong. Please try again"
              }
              title={"Failed to Update Client Information"}
            />,
          );
        });
    } else {
      createClient(formData)
        .unwrap()
        .then(() => {
          onClose();
          dispatch(clientsApi.util.invalidateTags([ClientTags.clients]));
        })
        .catch((err) => {
          toast.error(
            <CustomToast
              type={"error"}
              text={
                getErrorMessage(err).globalError ||
                "Something went wrong. Please try again"
              }
              title={"Failed to Create New Client"}
            />,
          );
        });
    }
  };

  const isDisabled = useMemo(
    () =>
      Object.values(errors).some(Boolean) ||
      requiredFields.some((key) => !formData[key].trim()),
    [errors, formData, requiredFields],
  );

  return (
    <ReuseModal
      isOpen
      onClose={onClose}
      onSubmit={submitHandler}
      title="Add New Client User"
      maxWidth="680px"
      submitBtnText={client ? "Save" : "Send invitation"}
      submitBtnColor="#1C3C6C"
      isDisabled={isDisabled || isLoading}
      isDisabledCancelBtn={isLoading}
      isLoading={isLoading}
    >
      <Stack gap="24px" pt="8px" pb="24px">
        <Typography
          variant="subtitle1"
          color="custom.textDarkBlue"
          fontWeight={500}
        >
          Personal Data
        </Typography>
        <Stack gap="48px">
          <Stack direction="row" gap="8px">
            <ReuseInput
              name="first_name"
              handleInputChange={inputChangeHandler}
              errorMessage={errors.first_name ? errorMessages.first_name : ""}
              value={formData.first_name}
              label="First Name"
              placeholder="First Name"
              isError={errors.first_name}
              isDisabled={isLoading}
            />

            <ReuseInput
              name="last_name"
              handleInputChange={inputChangeHandler}
              errorMessage={errors.last_name ? errorMessages.last_name : ""}
              value={formData.last_name}
              label="Last Name"
              placeholder="Last Name"
              isError={errors.last_name}
              isDisabled={isLoading}
            />
          </Stack>

          <Stack direction="row" gap="8px">
            <ReuseInput
              name="email"
              handleInputChange={inputChangeHandler}
              errorMessage={errors.email ? errorMessages.email : ""}
              value={formData.email}
              label="Job Email"
              placeholder="example@email.com"
              isError={errors.email}
              isDisabled={isLoading || !!client}
            />

            <ReuseInput
              name="job_title"
              handleInputChange={inputChangeHandler}
              errorMessage={errors.job_title ? errorMessages.job_title : ""}
              value={formData.job_title}
              label="Job Title"
              placeholder="Job Title"
              isError={errors.job_title}
              isDisabled={isLoading}
            />
          </Stack>
        </Stack>

        <Typography
          variant="subtitle1"
          color="custom.textDarkBlue"
          fontWeight={500}
        >
          Company Information
        </Typography>
        <Stack direction="row" gap="8px">
          <ReuseInput
            name="company_name"
            handleInputChange={inputChangeHandler}
            errorMessage={errors.company_name ? errorMessages.company_name : ""}
            value={formData.company_name}
            label="Company Name"
            placeholder="Company Name"
            isError={errors.company_name}
            isDisabled={isLoading}
          />
          <ReuseInput
            name="company_domain"
            handleInputChange={inputChangeHandler}
            errorMessage={
              errors.company_domain ? errorMessages.company_domain : ""
            }
            value={formData.company_domain}
            label="Company Domain"
            placeholder="Company Domain"
            isError={errors.company_domain}
            isDisabled={isLoading}
          />
        </Stack>

        <Typography
          variant="subtitle1"
          color="custom.textDarkBlue"
          fontWeight={500}
        >
          Additional Information
        </Typography>

        <ReuseInput
          name="notes"
          handleInputChange={inputChangeHandler}
          errorMessage={errors.notes ? errorMessages.notes : ""}
          value={formData.notes}
          label="Notes (Optional)"
          maxLength={NOTES_MAX_LENGTH}
          multiline
          placeholder="Notes (Optional)"
          isError={errors.notes}
          maxRows={6}
          isDisabled={isLoading}
        />
      </Stack>
    </ReuseModal>
  );
};
